import type { QueryRoom } from '../../types/Query';
import type { TravellerInfo } from '../../types/Quotation';

export type QuotationRoomOccupancy = {
  adults: number;
  children: number;
  childrenAges?: Array<number | null>;
};

export const buildDefaultQuotationRooms = (): QuotationRoomOccupancy[] => ([
  { adults: 2, children: 0, childrenAges: [] },
]);

export const normalizeQuotationRooms = (rooms?: QuotationRoomOccupancy[] | null): QuotationRoomOccupancy[] => {
  const normalized = Array.isArray(rooms)
    ? rooms.map((room) => {
      const children = Math.max(0, Number(room?.children || 0));
      const childrenAges = Array.isArray(room?.childrenAges)
        ? room.childrenAges.slice(0, children).map((age) => (
          age === null || age === undefined
            ? null
            : Math.max(0, Math.min(12, Number(age || 0)))
        ))
        : [];
      while (childrenAges.length < children) {
        childrenAges.push(null);
      }
      return {
        adults: Math.max(1, Number(room?.adults || 0)),
        children,
        childrenAges,
      };
    })
    : [];

  return normalized.length > 0 ? normalized : buildDefaultQuotationRooms();
};

export const fromQueryRooms = (rooms?: QueryRoom[] | null): QuotationRoomOccupancy[] => {
  const normalized = Array.isArray(rooms)
    ? rooms.map((room) => ({
      adults: Math.max(1, Number(room?.adults ?? room?.Adults ?? 0)),
      children: Math.max(0, Number(room?.children ?? room?.Children ?? 0)),
      childrenAges: Array.isArray(room?.childrenAges ?? room?.ChildrenAges)
        ? (room?.childrenAges ?? room?.ChildrenAges ?? []).map((age) => (
          age === null || age === undefined ? null : Math.max(0, Math.min(12, Number(age || 0)))
        ))
        : [],
    }))
    : [];

  return normalized.length > 0 ? normalizeQuotationRooms(normalized) : buildDefaultQuotationRooms();
};

export const toTravellerInfoFromRooms = (rooms?: QuotationRoomOccupancy[] | null): TravellerInfo => {
  const normalized = normalizeQuotationRooms(rooms);
  const adults = normalized.reduce((sum, room) => sum + Math.max(0, Number(room.adults || 0)), 0);
  const childBreakdown = normalized.reduce((totals, room) => {
    const roomChildren = Math.max(0, Number(room.children || 0));
    const infantCount = Array.isArray(room.childrenAges)
      ? room.childrenAges.filter((age) => typeof age === 'number' && age < 2).length
      : 0;
    return {
      children: totals.children + Math.max(0, roomChildren - infantCount),
      infants: totals.infants + infantCount,
    };
  }, { children: 0, infants: 0 });

  return { adults, children: childBreakdown.children, infants: childBreakdown.infants };
};

export const summarizeQuotationRooms = (rooms?: QuotationRoomOccupancy[] | null) => {
  const normalized = normalizeQuotationRooms(rooms);
  const roomCount = normalized.length;
  const adults = normalized.reduce((sum, room) => sum + Number(room.adults || 0), 0);
  const children = normalized.reduce((sum, room) => sum + Number(room.children || 0), 0);
  return `${roomCount} room${roomCount === 1 ? '' : 's'} ${adults} adult${adults === 1 ? '' : 's'} ${children} child`;
};

export const toHotelSearchRooms = (rooms?: QuotationRoomOccupancy[] | null) => (
  normalizeQuotationRooms(rooms).map((room) => ({
    Adults: Math.max(1, Number(room.adults || 0)),
    Children: Math.max(0, Number(room.children || 0)),
    ChildrenAges: Array.isArray(room.childrenAges)
      ? room.childrenAges
        .slice(0, Math.max(0, Number(room.children || 0)))
        .map((age) => (age === null || age === undefined ? 0 : Math.max(0, Math.min(12, Number(age || 0)))))
      : [],
  }))
);
